import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BookOpen, Search, FileText, ChevronRight, CheckCircle,
  Zap, Link2, AlertTriangle, RefreshCw, Database, MessageSquare,
  HelpCircle, ExternalLink, FolderOpen, Globe
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const knowledgeSources = [
  {
    icon: FileText,
    name: 'Notion',
    description: 'Sync workspaces, databases, and pages. Preserve structure and relationships.',
    features: ['Workspace sync', 'Database support', 'Page hierarchy', 'Block content']
  },
  {
    icon: FileText,
    name: 'Confluence',
    description: 'Index spaces, pages, and blog posts. Respect access permissions.',
    features: ['Space sync', 'Page content', 'Labels and tags', 'Access control']
  },
  {
    icon: FolderOpen,
    name: 'Google Drive',
    description: 'Index documents, spreadsheets, and presentations. OCR for images and PDFs.',
    features: ['Docs, Sheets, Slides', 'PDF extraction', 'Image OCR', 'Shared drives']
  },
  {
    icon: FolderOpen,
    name: 'SharePoint',
    description: 'Sync document libraries and lists. Maintain folder structure and metadata.',
    features: ['Document libraries', 'Lists', 'Metadata sync', 'Version history']
  },
  {
    icon: Database,
    name: 'ITSM Knowledge Bases',
    description: 'ServiceNow, Jira Service Management knowledge articles and solutions.',
    features: ['Article sync', 'Category structure', 'Solution linking', 'Freshness tracking']
  },
  {
    icon: Globe,
    name: 'Custom Sources',
    description: 'API-based ingestion for internal wikis, documentation sites, and custom systems.',
    features: ['REST API', 'Webhook triggers', 'Custom parsers', 'Scheduled sync']
  }
];

const searchFeatures = [
  {
    icon: Search,
    title: 'Semantic Search',
    description: 'Find answers even when exact keywords don\'t match. Understands intent and context.'
  },
  {
    icon: Link2,
    title: 'Source Citations',
    description: 'Every answer links back to source documents. Verify information at the source.'
  },
  {
    icon: RefreshCw,
    title: 'Freshness Indicators',
    description: 'See when content was last updated. Warnings for stale documentation.'
  },
  {
    icon: CheckCircle,
    title: 'Confidence Scores',
    description: 'Understand how certain the system is about each answer. Low confidence triggers human review.'
  }
];

const actionConversion = [
  {
    trigger: 'Answer identifies needed access',
    action: 'Create access request case',
    example: '"To access Salesforce, you need the Sales team role..." → Request Sales team access'
  },
  {
    trigger: 'Answer describes manual process',
    action: 'Run action bundle',
    example: '"To reset MFA, IT must verify identity and..." → Execute MFA reset workflow'
  },
  {
    trigger: 'Answer requires approval',
    action: 'Open approval request',
    example: '"Admin access requires manager approval..." → Submit approval request'
  },
  {
    trigger: 'Answer identifies gap',
    action: 'Flag for documentation',
    example: '"No documentation found for..." → Create documentation ticket'
  }
];

const gapDetection = [
  {
    type: 'Missing Documentation',
    description: 'Questions with no matching content flagged for documentation team.',
    metric: '15 gaps identified this month'
  },
  {
    type: 'Outdated Content',
    description: 'Documents not updated in threshold period surfaced for review.',
    metric: '23 articles marked stale'
  },
  {
    type: 'Contradictory Information',
    description: 'Multiple sources with conflicting answers flagged for reconciliation.',
    metric: '4 conflicts detected'
  },
  {
    type: 'Frequently Asked',
    description: 'Common questions without good answers prioritized for content creation.',
    metric: 'Top 10 FAQ gaps'
  }
];

const faqs = [
  {
    question: 'How does the system handle access-controlled content?',
    answer: 'Content permissions from source systems are respected. Users only see search results for content they have access to. Admin content is only shown to admins, team-specific docs to team members, etc.'
  },
  {
    question: 'How often is content synced from sources?',
    answer: 'Configurable per source. Options include real-time (webhook), scheduled (hourly/daily), or on-demand. Most organizations use hourly sync for knowledge bases and real-time for critical documentation.'
  },
  {
    question: 'Can the system answer questions that span multiple documents?',
    answer: 'Yes. The system synthesizes information from multiple sources when needed and cites all relevant documents. Users see which parts of the answer came from which source.'
  },
  {
    question: 'How do you ensure answer quality?',
    answer: 'Multiple mechanisms: confidence scores, source citations, freshness indicators, and human feedback loops. Low-confidence answers are flagged for human review before surfacing.'
  },
  {
    question: 'What happens when documentation doesn\'t exist for a question?',
    answer: 'The system acknowledges the gap, suggests alternative resources if available, and logs the query as a documentation gap. Content teams receive weekly reports of top documentation needs.'
  }
];

export const Knowledge = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/product" className="hover:text-foreground">Product</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Knowledge</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
              Knowledge Ingestion & Q&A
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl leading-relaxed">
              Ingest documentation from Notion, Confluence, SharePoint, and more.
              Search across all sources. Cite answers. Turn knowledge into action.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="default">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="default" variant="outline">
                <Link to="/docs">View Documentation</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Knowledge Sources */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Knowledge Sources</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {knowledgeSources.map((source, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <source.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground mb-1">{source.name}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{source.description}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {source.features.map((feature, fidx) => (
                      <span
                        key={fidx}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary text-xs text-muted-foreground rounded-md"
                      >
                        <CheckCircle className="w-3 h-3" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Search Features */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Search & Cite</h2>
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div className="space-y-6">
                {searchFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <feature.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground mb-1">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-secondary/30 border border-border rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Search className="w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    defaultValue="How do I request admin access to AWS?"
                    readOnly
                    className="flex-1 text-sm text-foreground bg-transparent border-none outline-none"
                  />
                </div>
                <div className="border-t border-border pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                      High Confidence
                    </span>
                    <span className="text-xs text-muted-foreground">3 sources</span>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed mb-4">
                    AWS admin access requires Security team approval. Submit a request through the Access Portal
                    with business justification. Approvals typically complete within 24 hours for standard requests.
                    Break-glass access is available for emergencies.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs">
                      <FileText className="w-3 h-3 text-muted-foreground" />
                      <span className="text-primary hover:underline cursor-pointer">AWS Access Policy</span>
                      <span className="text-muted-foreground">Updated 2 weeks ago</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <FileText className="w-3 h-3 text-muted-foreground" />
                      <span className="text-primary hover:underline cursor-pointer">Security Approval Guide</span>
                      <span className="text-muted-foreground">Updated 1 month ago</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Turn Into Action */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Zap className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-foreground">Turn Into Action</h2>
            </div>
            <p className="text-muted-foreground mb-8 max-w-3xl">
              When answers describe processes that can be automated, offer to execute them directly.
              One click from answer to action.
            </p>
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <div className="grid grid-cols-12 bg-secondary px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border">
                <div className="col-span-3">Trigger</div>
                <div className="col-span-3">Action</div>
                <div className="col-span-6">Example</div>
              </div>
              {actionConversion.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 px-6 py-4 border-b border-border last:border-0 text-sm hover:bg-secondary/30 transition-colors">
                  <div className="col-span-3 font-medium text-foreground">{item.trigger}</div>
                  <div className="col-span-3 text-muted-foreground">{item.action}</div>
                  <div className="col-span-6 text-muted-foreground font-mono text-xs">{item.example}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gap Detection */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <AlertTriangle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-foreground">Knowledge Gap Detection</h2>
            </div>
            <p className="text-muted-foreground mb-8 max-w-3xl">
              Automatically identify missing, outdated, or contradictory documentation.
              Prioritize content creation based on actual user needs.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {gapDetection.map((gap, idx) => (
                <div key={idx} className="bg-secondary/30 border border-border rounded-lg p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-foreground">{gap.type}</h3>
                    <span className="text-xs text-primary font-medium">{gap.metric}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{gap.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Empty State Example */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8 text-center">Graceful Empty States</h2>
            <div className="bg-white border border-border rounded-lg p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Search className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-2">No documentation found</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                We couldn't find documentation for "VPN setup for contractors."
                This gap has been logged for the documentation team.
              </p>
              <div className="flex justify-center gap-3">
                <Button size="sm" variant="outline">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Ask IT Support
                </Button>
                <Button size="sm" variant="outline">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Search Confluence
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-secondary/30 border border-border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-2 flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {faq.question}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-6">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-primary/5 border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Unify Your Knowledge</h2>
            <p className="text-muted-foreground mb-8">
              See how organizations consolidate documentation and reduce time-to-answer.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/product/itsm">Ticketing & ITSM</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default Knowledge;
